/**
 * Pulsante "Allegati (n)" nell'intestazione della cronologia della scheda Lead/Cliente:
 * apre un elenco di TUTTI gli allegati delle note, dal più recente, con Apri, Scarica
 * e "Vai alla nota" (scorre fino alla card e la evidenzia per un attimo).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ChatTooltip, tip, iconTip } from '../../messages/ChatTooltip';
import { AttachmentFileRow } from './AttachmentFileRow';
import {
  collectNoteAttachments,
  type NoteWithAttachments,
} from '../../../lib/utils/noteAttachmentFiles';

/** id DOM della card nota, usato da "Vai alla nota" */
export const noteCardDomId = (noteId: string) => `lead-note-${noteId}`;

interface LeadAttachmentsButtonProps {
  notes: NoteWithAttachments[] | null | undefined;
}

const noteDateLabel = (iso?: string) => {
  if (!iso) return undefined;
  try {
    return `Nota del ${format(parseISO(iso), 'dd/MM/yyyy')}`;
  } catch {
    return undefined;
  }
};

export const LeadAttachmentsButton: React.FC<LeadAttachmentsButtonProps> = ({ notes }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const items = useMemo(() => collectNoteAttachments(notes), [notes]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  const goToNote = (noteId: string) => {
    setOpen(false);
    const el = document.getElementById(noteCardDomId(noteId));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-[#337EA9]/40');
    window.setTimeout(() => el.classList.remove('ring-2', 'ring-[#337EA9]/40'), 1600);
  };

  return (
    <div ref={rootRef} className="relative">
      <ChatTooltip />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${
          open
            ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10 text-[#37352F] dark:text-white'
            : 'text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/5'
        }`}
        {...tip('Tutti gli allegati delle note')}
      >
        <Paperclip className="h-3.5 w-3.5" strokeWidth={1.5} />
        Allegati
        <span className="rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-white/10 px-1.5 text-[10px] font-semibold">
          {items.length}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-[360px] max-w-[80vw] rounded-lg border border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] bg-[#FBFBFA] dark:bg-[#252525] shadow-lg">
          <div className="flex items-center justify-between border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] px-3 py-2">
            <span className="text-[12px] font-semibold text-[#37352F] dark:text-white">
              Allegati delle note ({items.length})
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10"
              {...iconTip('Chiudi')}
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="max-h-[360px] space-y-1.5 overflow-y-auto p-2">
            {items.map((att) => (
              <AttachmentFileRow
                key={att.id}
                attachment={att}
                subtitle={noteDateLabel(att.noteCreatedAt)}
                onGoToNote={() => goToNote(att.noteId)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
